import { ExternalLink } from 'lucide-react'

import { InstallerDownloadCards } from 'components/ServerEditions/InstallerDownloadCards'
import { ServerTokenDisplay } from 'components/ServerEditions/ServerTokenDisplay'
import { serverEditionsRoutePath } from 'config/routePaths'
import { Badge, ButtonLink, Icon } from 'ui'
import { formatCurrency, formatDate } from 'utils/billing'
import { isPaidAccount } from 'utils/validation'

const INSTALL_GUIDE_URL = 'https://docs.expandrive.com/server-edition'

const HeaderStatusItem = ({ children }) => (
  <span className="tw:inline-flex tw:items-center tw:gap-2">
    <span
      aria-hidden="true"
      className="tw:inline-flex tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-status-success-surface"
    >
      <span className="tw:h-2 tw:w-2 tw:rounded-full tw:bg-status-success-foreground" />
    </span>
    <span>{children}</span>
  </span>
)

export const getCompletedPaymentDetails = (summary = {}) => {
  const billingSummary = summary || {}
  const history = billingSummary.history || []
  const payment = history.find(item => item.type === 'payment')
  const invoice = history.find(item => item.type === 'invoice' && item.hasPdf)
  const subscription = billingSummary.subscription || {}

  return {
    amount: formatCurrency(
      payment?.amount ?? subscription.rate,
      payment?.currency ?? billingSummary.balance?.currency,
    ),
    invoice,
    renewalDate: subscription.expirationDate ? formatDate(subscription.expirationDate) : null,
  }
}

export const PaymentCompleteHeaderMeta = ({ account = {}, summary }) => {
  const { amount, renewalDate } = getCompletedPaymentDetails(summary)
  const serverToken = account.serverToken || account.server_token || ''

  return (
    <span className="tw:flex tw:flex-wrap tw:gap-x-4 tw:gap-y-2">
      <HeaderStatusItem>{amount} paid{renewalDate ? ` · renews ${renewalDate}` : ''}</HeaderStatusItem>
      {serverToken && <HeaderStatusItem>Server token ready</HeaderStatusItem>}
    </span>
  )
}

export const PaymentComplete = ({
  account = {},
}) => {
  const serverToken = account.serverToken || account.server_token || ''
  const canOpenConsole = isPaidAccount(account)

  return (
    <div>
      <section>
        <h2 className="ds-heading-3">Download Server Edition</h2>
        <p className="ds-helper tw:mt-1 tw:mb-5">Choose an installer for the server you want to activate.</p>
        <InstallerDownloadCards />
      </section>

      <section className="ds-page-hero tw:mt-6 tw:grid tw:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.9fr)] tw:overflow-hidden tw:rounded-card tw:shell:grid-cols-1">
        <div className="tw:p-5 tw:mobile:p-4">
          <p className="tw:text-sm tw:font-semibold tw:text-foreground-subtle">Next Steps</p>
          <h2 className="ds-heading-3 tw:mt-2">Set Up Your First Server</h2>
          <p className="tw:mt-3 tw:max-w-[52ch] tw:text-foreground-muted">
            Install the package, register this subscription, and mount your first share.
          </p>
          <div className="tw:mt-5 tw:flex tw:flex-wrap tw:items-center tw:gap-3">
            <ButtonLink
              href={INSTALL_GUIDE_URL}
              icon={<Icon as={ExternalLink} />}
              rel="noreferrer"
              target="_blank"
              variant="secondary"
            >
              Open the Install Guide
            </ButtonLink>
            {canOpenConsole && (
              <ButtonLink to={serverEditionsRoutePath} variant="ghost">Skip to the console</ButtonLink>
            )}
          </div>
        </div>

        <div className="tw:border-l tw:border-default tw:bg-surface-muted tw:p-5 tw:shell:border-l-0 tw:shell:border-t tw:mobile:p-4">
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-4">
            <h2 className="tw:text-sm tw:font-semibold tw:text-foreground">Server Token</h2>
            <Badge dot tone="green">Active</Badge>
          </div>
          <div className="tw:mt-4">
            <ServerTokenDisplay token={serverToken} />
          </div>
          <p className="tw:mt-4 tw:text-sm tw:text-foreground-subtle">
            You need this once during setup. It remains available under Server Editions.
          </p>
        </div>
      </section>
    </div>
  )
}

export default PaymentComplete
